import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DragPreviewProps } from "./types";
import { getStorageUrl } from "@utils/utils";
import { nodeColors } from "./constants";
import useStore from "../../store/useStore";
import { MetaRouterIcon } from "@/config/icons";

export const DragPreview: React.FC<DragPreviewProps> = ({ item, position }) => {
  const Icon = item.icon;
  const nodeType = item.nodeType;
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  
  // Get nodes from the store using a stable reference
  const nodes = useStore((state) => state.nodes);
  
  // Memoize the group nodes to prevent creating a new array on every render
  const groupNodes = useMemo(() => 
    nodes.filter(node => node.type === 'group'),
    [nodes]
  );
  
  // Use a ref to track the previous hovered group ID to prevent unnecessary updates
  const prevHoveredGroupIdRef = useRef<string | null>(null);
  
  // Check if the drag preview is over a group node
  useEffect(() => {
    // Find if the current position is over any group node
    const isOverGroup = groupNodes.find(groupNode => {
      if (!groupNode.position || !groupNode.width || !groupNode.height) return false;
      
      return (
        position.x >= groupNode.position.x &&
        position.x <= groupNode.position.x + groupNode.width &&
        position.y >= groupNode.position.y &&
        position.y <= groupNode.position.y + groupNode.height
      );
    });
    
    const newHoveredGroupId = isOverGroup ? isOverGroup.id : null;
    
    // Only update state if the hovered group has changed
    if (prevHoveredGroupIdRef.current !== newHoveredGroupId) {
      setHoveredGroupId(newHoveredGroupId);
      prevHoveredGroupIdRef.current = newHoveredGroupId;
      
      // If we're over a group, update the group's state to show it's being hovered
      if (isOverGroup) {
        // We can't directly modify the group node's state here, but we can
        // use a custom event to notify the GroupNode component
        const customEvent = new CustomEvent('dragPreviewOverGroup', {
          detail: { groupId: isOverGroup.id }
        });
        window.dispatchEvent(customEvent);
      }
    }
  }, [position.x, position.y, groupNodes]);
  
  // Create a simplified preview based on node type
  const renderPreview = () => {
    // Cluster node preview
    if (nodeType === "cluster") {
      const colors = nodeColors["cluster"];
      return (
        <div className={`pl-4 pr-4 py-3 w-[224px] rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-8 h-8 rounded-md ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark} flex items-center justify-center flex-shrink-0`}>
              <MetaRouterIcon size={16} />
            </div>
            <div>
              <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.name}</h3>
              <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                MetaRouter Managed
              </span>
            </div>
          </div>
        </div>
      );
    }
    
    // Source node preview
    else if (nodeType === "source") {
      const colors = nodeColors["source"];
      return (
        <div className={`pl-4 pr-4 py-3 rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark} flex items-center justify-center flex-shrink-0`}>
              {Icon && <Icon className="h-4 w-4" />}
            </div>
            <div>
              <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.name}</h3>
            </div>
          </div>
        </div>
      );
    }
    
    // Destination node preview
    else if (nodeType === "destination") {
      return (
        <div className={`pl-4 pr-4 py-3 rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex items-center gap-3">
            {item.integration && (
              <div className="w-8 h-8 rounded-md flex-shrink-0">
                <img
                  src={getStorageUrl(item.integration.logo_url || '')}
                  alt={item.name}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div>
              <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.name}</h3>
            </div>
          </div>
        </div>
      );
    }
    
    // Text node preview
    else if (nodeType === "text-node") {
      return (
        <div className={`w-[200px] h-[80px] rounded-md bg-white dark:bg-gray-800 border-2 border-blue-500 shadow-sm ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex flex-col h-full w-full p-2">
            <div className="text-sm text-gray-400 dark:text-gray-500 italic">Enter text...</div>
            {/* Resize handles in corners */}
            <div className="absolute top-0 right-0 w-2 h-2 bg-blue-500 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 rounded-full transform translate-x-1/2 translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-2 h-2 bg-blue-500 rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
            <div className="absolute top-0 left-0 w-2 h-2 bg-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          </div>
        </div>
      );
    }
    // Notes node preview
    else if (nodeType === "notes-node") {
      return (
        <div className={`w-[200px] h-[80px] rounded-xl bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 shadow-sm dark:shadow-sm-dark ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex flex-col h-full w-full p-3">
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-1">Notes</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">Add notes here...</div>
          </div>
        </div>
      );
    }
    // Default preview for other node types
    else {
      // Get colors based on node type, or use event colors as fallback
      const colors = nodeColors[nodeType] || nodeColors["event"];
      return (
        <div className={`pl-4 pr-4 py-3 rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm ${hoveredGroupId ? 'ring-3 ring-blue-500/70' : ''}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-md ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark} flex items-center justify-center flex-shrink-0`}>
              {Icon && <Icon className="h-4 w-4" />}
            </div>
            <div>
              <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.name}</h3>
            </div>
          </div>
        </div>
      );
    }
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed pointer-events-none z-50"
        style={{
          left: position.x - 50, // Offset to center under cursor
          top: position.y - (nodeType === "notes-node" || nodeType === "text-node" ? 40 : 72),  // Reduced offset for Notes and Text nodes
          width: '200px',
          transform: 'scale(1)',
          transformOrigin: 'top left'
        }}
      >
        {renderPreview()}
      </motion.div>
    </AnimatePresence>
  );
};
